import { Mixpanel } from "mixpanel-react-native";
// The SDK has no public custom-transport hook. Keep this integration covered by
// the SDK contract test when upgrading mixpanel-react-native.
// @ts-expect-error The SDK's internal JavaScript transport has no declaration.
import { MixpanelNetwork } from "mixpanel-react-native/javascript/mixpanel-network";
import { config } from "../config";
import { createMixpanelTransport } from "./mixpanelTransport";

const trackAutomaticEvents = false;
const MIXPANEL_TOKEN =
  process.env.MIXPANEL_TOKEN ?? "29033d5c7446c7d60657f6e4ada2e900";

MixpanelNetwork.sendRequest = createMixpanelTransport(
  `${config.coreApi.protocol}://${config.coreApi.host}/api/v1/events`,
);

export const mixpanel = new Mixpanel(MIXPANEL_TOKEN, trackAutomaticEvents);

let sessionEmail: string | null = null;
let sessionClinicId: string | null = null;

const normalizeEmail = (email?: string | null): string | null => {
  if (!email) return null;
  const trimmedEmail = email.trim();
  return trimmedEmail.length ? trimmedEmail : null;
};

export const setMixpanelSessionEmail = (email?: string | null) => {
  sessionEmail = normalizeEmail(email);
};

const normalizeClinicId = (clinicId?: unknown): string | null => {
  if (typeof clinicId !== "string") return null;
  const trimmedClinicId = clinicId.trim();
  return trimmedClinicId.length ? trimmedClinicId : null;
};

export const setMixpanelSessionClinicId = (clinicId?: string | null) => {
  sessionClinicId = normalizeClinicId(clinicId);
};

const originalTrack = mixpanel.track.bind(mixpanel);

mixpanel.track = (
  eventName: string,
  properties?: Record<string, unknown> | undefined,
) => {
  const normalizedSessionEmail = normalizeEmail(sessionEmail);
  const eventProperties =
    properties && typeof properties === "object" ? { ...properties } : {};

  if (!("User Email" in eventProperties) && normalizedSessionEmail) {
    eventProperties["User Email"] = normalizedSessionEmail;
  }

  const eventClinicId =
    normalizeClinicId(eventProperties["Clinic Id"]) ??
    normalizeClinicId(eventProperties["Clinic ID"]) ??
    normalizeClinicId(eventProperties.clinic_uuid) ??
    normalizeClinicId(eventProperties.clinic_id) ??
    normalizeClinicId(eventProperties.clinicId) ??
    normalizeClinicId(sessionClinicId);

  if (eventClinicId) {
    eventProperties["Clinic Id"] = eventClinicId;
  }

  return originalTrack(eventName, eventProperties);
};

/**
 * Track an analytics event, attaching the session's email and clinic details.
 * `mixpanel.track` already injects "User Email" from the active session, so we
 * only add session fields here when they're explicitly provided (and let event
 * props win over them).
 */
export const trackEvent = (
  eventName: string,
  eventProps: Record<string, unknown> = {},
  session?: {
    userEmail?: string | null;
    clinicId?: string | null;
    clinicName?: string | null;
  } | null,
) => {
  const sessionProps: Record<string, unknown> = {};
  if (session?.userEmail) sessionProps["User Email"] = session.userEmail;
  if (session?.clinicId) sessionProps["Clinic Id"] = session.clinicId;
  if (session?.clinicName) sessionProps["Clinic Name"] = session.clinicName;

  mixpanel.track(eventName, { ...sessionProps, ...eventProps });
};
